/* In-page jumps, theme wide.
 *
 * Abhishek's note on 20 Sep was that the label is the critical asset and
 * everything that promises it has to click through to it. That means the jump
 * can no longer live inside one section's own <script>, which is where it was:
 * evidence-badges22 had a private copy bound to its own root, so a claim in the
 * ticker or a line in another section had no way to reach the same landing.
 *
 * Anything carrying data-silt-jump="<selector>" now lands on that element.
 * An element with no selector falls back to its own href="#id".
 *
 * Two things the naive version gets wrong and this one does not:
 *
 *   1. The header is sticky and its height changes between phone and desktop,
 *      so the offset is measured at click time, never assumed.
 *   2. If the jump also switches the AM/PM mode (data-drinksilt-theme-set),
 *      the sections that only exist in the other mode have not laid out yet,
 *      and the target moves under a scroll that is already running. So the
 *      scroll waits a beat, then corrects once after everything settles.
 *      Without a mode switch there is nothing to wait for and it goes at once.
 */
(function () {
  'use strict';

  var MODE_SETTLE_MS = 260;
  var CORRECTION_MS = 1100;
  var GAP = 12;

  function isVisible(el) {
    if (!el) return false;
    var box = el.getBoundingClientRect();
    return box.width > 0 && box.height > 0;
  }

  /* The selector is a list in priority order, and the FIRST VISIBLE match wins,
     not the first in the document. This is what makes "read the label" mean
     something in all six states, because there is no one label element:
     the ingredients panel is hidden on a phone in Mornings and Nights (it
     repeated the formula), and Combo has never shown it at all. So the same
     link reads "the label, or failing that the doses, or failing that the
     formula in the hero", and lands on whichever of those is on the page. */
  function targetFor(link) {
    var selector = link.getAttribute('data-silt-jump');

    if (!selector) {
      var href = link.getAttribute('href') || '';
      var hash = href.indexOf('#');
      if (hash === -1 || hash === href.length - 1) return null;
      selector = href.slice(hash);
    }

    var candidates = selector.split(',');
    var first = null;
    var fallback = null;

    for (var i = 0; i < candidates.length; i++) {
      var one = candidates[i].trim();
      if (!one) continue;
      /* A list may end on a page URL (27 Sep audit): the place to go when
         nothing in the list is showing here, e.g. "read the full label" on
         the Shop page or a Combo phone, where no label panel exists. */
      if (one.charAt(0) === '/' || one.indexOf('://') !== -1) { fallback = one; continue; }

      var el;
      try {
        el = document.querySelector(one);
      } catch (error) {
        continue;
      }

      if (!el) continue;
      if (isVisible(el)) return el;
      if (!first) first = el;
    }

    /* Nothing visible, and the list names a page to go to instead: go. */
    if (fallback) return null;

    /* Nothing visible. A hidden element is still better than refusing to move
       when the click was a mode switch: the section it wants may be about to
       appear. Without a mode switch this lands on a collapsed element, which
       is the same place the browser's own anchor would go. */
    return first;
  }

  function headerOffset() {
    var header = document.querySelector('[data-drinksilt-header]');
    if (!header) return GAP;

    var box = header.getBoundingClientRect();
    /* A header that has scrolled away is not covering the landing. Only a
       stuck one costs height. */
    if (box.bottom <= 0) return GAP;
    return box.height + GAP;
  }

  function land(target, behavior) {
    var top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
    window.scrollTo({ top: Math.max(top, 0), behavior: behavior });
  }

  /* Move the keyboard caret with the page. tabindex="-1" makes a plain section
     focusable; preventScroll keeps focus() from fighting the smooth scroll. */
  function focusTarget(target) {
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    try {
      target.focus({ preventScroll: true });
    } catch (error) {
      target.focus();
    }
  }

  /* Named landings for links that cannot carry data-silt-jump, such as
     the admin-built footer menus (26 Sep). "/#daily-rituals" lands on the
     How to use section of whichever tab is showing, from the homepage or
     arriving from another page. "/#shop" (27 Sep, the Story page's
     button) lands on the pack cards. Since the 3 Oct pillars homepage the
     How to use sections are off the homepage, so the Ritual band answers it.
     7 Oct: that band is gone; on Combo it lands on "Easy to stick to."
     (the pillars' third band), her pick. Mornings / Nights keep How to use. */
  var ALIASES = {
    '#daily-rituals': '#pillar-ritual, .htu, [id^="tq-"] .tq-band--3',
    '#shop': '.drinksilt-pricing, [id$=silt_variant_grid_top]',
    /* The footer's "No proprietary blends" (admin menu) points here; the
       Science page names every dose in its ingredients band. */
    '#proprietary-blend': '#no-blends'
  };

  function aliasTarget(hash) {
    var sel = ALIASES[hash];
    if (!sel) return null;
    var list = document.querySelectorAll(sel);
    for (var i = 0; i < list.length; i++) if (isVisible(list[i])) return list[i];
    return list[0] || null;
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href*="#"]');
    if (!link || link.hasAttribute('data-silt-jump')) return;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    var url;
    try { url = new URL(link.href, window.location.href); } catch (error) { return; }
    if (url.pathname !== window.location.pathname) return;
    var target = aliasTarget(url.hash);
    if (!target) return;
    event.preventDefault();
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    land(target, reduced ? 'auto' : 'smooth');
    focusTarget(target);
  });

  function landOnLoadAlias() {
    var target = aliasTarget(window.location.hash);
    if (!target) return;
    /* Let the mode-gated sections lay out, then correct once. */
    window.setTimeout(function () { land(target, 'auto'); }, 350);
    window.setTimeout(function () { land(target, 'auto'); }, 1200);
  }
  if (document.readyState === 'complete') landOnLoadAlias();
  else window.addEventListener('load', landOnLoadAlias);

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-silt-jump]');
    if (!link) return;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;

    var target = targetFor(link);
    /* No target means the href, if there is one, is still worth following. */
    if (!target) return;

    event.preventDefault();

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var behavior = reduced ? 'auto' : 'smooth';
    var switchesMode = link.hasAttribute('data-drinksilt-theme-set');

    if (!switchesMode) {
      land(target, behavior);
      focusTarget(target);
      return;
    }

    /* The theme manager's own delegated handler has applied the mode by the
       time this runs. Give the mode-gated sections a beat to lay out. */
    window.setTimeout(function () {
      land(target, behavior);
      focusTarget(target);
    }, MODE_SETTLE_MS);

    window.setTimeout(function () {
      land(target, 'auto');
    }, CORRECTION_MS);
  });
})();
